import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Login.css';
import { useAuth } from '../context/AuthContext';
import { ACCOUNT_CONSOLE_URL } from '../auth/oidcConfig';

// Zwei der abgewiesenen Faelle kann die Person selbst aufloesen, und bis hierher
// sagte ihr das niemand: Der Organisationsname war beim Einrichten schon
// vergeben. Das Feld liegt in Keycloaks Kontoverwaltung und ist dort aenderbar.
//
// Seit die Registrierungsmaske den Namen selbst prueft, ist das der seltene
// Rest — eine Kollision, die genau zwischen Registrierung und Einrichtung
// entstanden ist. Selten heisst nicht, dass man darin steckenbleiben darf.
const SELBST_LOESBAR = new Set(['ORG_NAME_TAKEN', 'ORG_NAME_DERIVED_TAKEN']);

// Kein Formular mehr: Das Passwort wird ausschliesslich bei Keycloak
// eingegeben. Diese Seite ist nur noch Startpunkt und Rueckkehrpunkt der
// Anmeldung — und die Stelle, an der sichtbar wird, warum jemand trotz
// geglueckter Anmeldung nicht hineinkommt.
const Login = () => {
  const {
    isLoggedIn, isAuthLoading, isReady, setup, authError,
    signIn, handleLogout, erneutVersuchen,
  } = useAuth();
  const navigate = useNavigate();

  const abgelaufen = React.useMemo(() => {
    const nachricht = localStorage.getItem('sessionExpiredMessage');
    if (nachricht) localStorage.removeItem('sessionExpiredMessage');
    return nachricht || '';
  }, []);

  const abgemeldet = React.useMemo(() => {
    const merker = localStorage.getItem('abgemeldet');
    if (merker) localStorage.removeItem('abgemeldet');
    return Boolean(merker);
  }, []);

  // Erst wenn die Einrichtung durch ist. Vorher weiterzuleiten war der Fehler,
  // der eine unbestaetigte Anmeldung in die Oberflaeche liess: Dieser Effekt
  // laeuft als Kind-Effekt VOR dem Einrichtungs-Effekt im AuthProvider, sah
  // also nur "angemeldet" und schickte weiter, bevor der BFF ueberhaupt
  // gefragt worden war.
  useEffect(() => {
    if (isReady) navigate('/home');
  }, [isReady, navigate]);

  // Diese Seite hat nur einen Grund, stehen zu bleiben: wenn sie etwas zu sagen
  // hat. Sonst ist sie ein Knopf, der immer dasselbe tut — also fuehrt sie von
  // sich aus weiter.
  const etwasZuSagen = Boolean(abgelaufen || abgemeldet || authError);

  // Nur einmal je Aufruf der Seite. Ohne die Sperre koennte ein erneutes
  // Rendern waehrend der laufenden Weiterleitung sie ein zweites Mal anstossen.
  const weitergeleitet = React.useRef(false);
  useEffect(() => {
    if (etwasZuSagen || isLoggedIn || isAuthLoading || weitergeleitet.current) return;
    weitergeleitet.current = true;
    signIn();
  }, [etwasZuSagen, isLoggedIn, isAuthLoading, signIn]);

  // Angemeldet, aber der BFF laesst nicht durch. "blocked" ist eine
  // Entscheidung — die Adresse ist unbestaetigt, oder das Konto gehoert schon
  // zu einem fremden Tenant —, "error" eine Stoerung. Der Text kommt in beiden
  // Faellen vom BFF; hier entscheidet sich nur, welche Knoepfe sinnvoll sind.
  const abgewiesen = isLoggedIn && (setup.status === 'blocked' || setup.status === 'error');

  // Ein zweiter Anlauf hilft, wenn sich in der Zwischenzeit etwas geaendert
  // haben kann: bei einer Stoerung ohnehin, und bei der unbestaetigten Adresse,
  // sobald der Link im Postfach angeklickt wurde. Bei allem anderen muss erst
  // jemand anderes taetig werden, und ein Knopf, der nichts bewegt, waere eine
  // Einladung zum Klicken.
  // Nach einer Aenderung in der Kontoverwaltung gehoert der zweite Anlauf
  // ebenfalls dazu: Der Organisationsname reist im Token mit, und erneutVersuchen
  // holt ueber signinSilent ein frisches. Ohne das traegt der alte Token weiter
  // den alten Namen und die Einrichtung scheitert genauso wie zuvor.
  const selbstLoesbar = setup.status === 'blocked' && SELBST_LOESBAR.has(setup.code);

  const nochmalSinnvoll =
    setup.status === 'error' || setup.code === 'AUTH_EMAIL_NOT_VERIFIED' || selbstLoesbar;

  return (
    <div className="login-container">
      <div className="login-box">
        <h1> Open KI IoT Hub </h1>

        <h2>Einloggen</h2>

        {abgelaufen && <div className="login-error" role="alert">{abgelaufen}</div>}
        {abgewiesen && <div className="login-error" role="alert">{setup.message}</div>}
        {authError && <div className="login-error" role="alert">{authError}</div>}

        {isAuthLoading ? (
          <p>Anmeldung wird geprüft…</p>
        ) : abgewiesen ? (
          <>
            {selbstLoesbar && (
              <p className="login-hilfe">
                Der Name lässt sich in der Kontoverwaltung ändern:{' '}
                <a href={ACCOUNT_CONSOLE_URL} target="_blank" rel="noopener noreferrer">
                  Organisation bearbeiten
                </a>
                . Danach hier auf „Ich habe den Namen geändert“.
              </p>
            )}
            {nochmalSinnvoll && (
              <button type="button" onClick={erneutVersuchen}>
                {setup.code === 'AUTH_EMAIL_NOT_VERIFIED'
                  ? 'Ich habe die Adresse bestätigt'
                  : selbstLoesbar
                    ? 'Ich habe den Namen geändert'
                    : 'Erneut versuchen'}
              </button>
            )}
            <div className="login-links">
              <button type="button" className="link-button" onClick={handleLogout}>
                Abmelden
              </button>
            </div>
          </>
        ) : isLoggedIn ? (
          // Angemeldet und noch nichts entschieden: /provision laeuft.
          <p>Arbeitsbereich wird eingerichtet…</p>
        ) : etwasZuSagen ? (
          <>
            <p>Die Anmeldung erfolgt zentral über Keycloak.</p>
            <button type="button" onClick={signIn}>
              Mit Keycloak anmelden
            </button>
            <div className="login-links">
              <span className="link-hint">
                Registrierung, Passwort zurücksetzen und Zwei-Faktor liegen dort.
              </span>
            </div>
          </>
        ) : (
          <p>Weiterleitung zu Keycloak…</p>
        )}

        {/* Der Notausgang /logout bleibt als Route bestehen (siehe Logout.js),
            aber nicht mehr als Angebot auf dieser Seite: er stand unter jedem
            Zustand, also auch unter denen, in denen es nichts freizuräumen gab,
            und tat dann sichtbar nichts. Wer ihn braucht, wird darauf
            hingewiesen; wer ihn nicht braucht, liest ihn nicht mehr. */}
      </div>
    </div>
  );
};

export default Login;
